import { BaseModal, Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { BrandType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ENUM_CATEGORY } from 'utils/enum';
import PageText from './base/page-text';
import useDashboardContext from 'contexts/dashboard.context';
import { handleApiError } from 'utils/functions';

const AddNewBrandModal = ({
  show,
  onClose,
  refresh
}: {
  show: boolean;
  onClose: () => void;
  refresh?: () => void;
}) => {
  const { activeCokitchen } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brands, setBrands] = useState([] as BrandType[]);
  const [selectedBrands, setSelectedBrands] = useState<string[] | undefined>([]);
  const [cardHovered, setCardHovered] = useState<number>();

  const getActiveBrands = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`brands/pop-cokitchen/${activeCokitchen?.id} `);
      const result = res.data.data.map((item: BrandType) => item.id);
      setBrands(brands.filter((item) => !result.includes(item.id)));
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getActiveBrands();
  }, [activeCokitchen]);

  const onConfirm = async () => {
    setLoading(true);
    try {
      const res = await apiService.put(`marketing/pop/cokitchen`, {
        data: {
          cokitchen_id: activeCokitchen.id,
          active: true,
          brand_ids_to_add: selectedBrands,
          brand_ids_to_remove: []
        }
      });
      if (res?.data) {
        refresh?.();

        toast(
          <ToastMessage
            type='success'
            message={`${activeCokitchen.name} brands successfully updated`}
          />
        );

        onClose();
      }
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBrands = async () => {
    if (brands.length > 0) return;
    setBrandsLoading(true);
    try {
      const params = {
        type: 'FOOD',
        archived: false
      };
      const res = await apiService.get('brands/kitchen', {
        params,
        isInternal: true
      });
      const unarchivedBrands = res.data?.data
        ?.filter(
          ({ archived, item_type }: BrandType) =>
            !archived && item_type === ENUM_CATEGORY.FOOD.toUpperCase()
        )
        .sort((a: BrandType, b: BrandType) => a.name.localeCompare(b.name));
      setBrands(unarchivedBrands);
    } finally {
      setBrandsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllBrands();
  }, []);
  const handleChange = (id: string) => {
    if (!selectedBrands?.includes(id)) {
      selectedBrands && setSelectedBrands([...selectedBrands, id]);
    } else {
      setSelectedBrands((prev: any) => prev.filter((item: string) => item !== id));
    }
  };
  return (
    <div>
      <BaseModal
        isOpen={show}
        title={`Add ${activeCokitchen?.name} brands`}
        onRequestClose={() => onClose()}
        footer={
          <Button
            onClick={onConfirm}
            className='capitalize'
            color='primary'
            variant='block'
            disabled={brandsLoading || selectedBrands?.length === 0 || !selectedBrands}
            loading={loading}
            small>
            Confirm
          </Button>
        }>
        {brandsLoading ? (
          <PageText text='Brands loading...' />
        ) : (
          brands.map((item, index) => (
            <div
              onMouseEnter={() => setCardHovered(index)}
              onMouseLeave={() => setCardHovered(index)}
              onClick={() => {
                handleChange(item.id);
              }}
              className={
                'my-[12px] relative border border-[#EDEDEE] rounded-[10px] cursor-pointer p-[16px]'
              }
              key={index}>
              {(cardHovered === index || selectedBrands?.includes(item.id)) && (
                <div className='absolute top-3 left-4'>
                  <Input
                    type='checkbox'
                    name='add-brand'
                    checked={selectedBrands?.includes(item.id)}
                    readOnly
                  />
                </div>
              )}
              <div className='flex items-center gap-[14px]'>
                <div
                  className='h-[45px] w-[50px] rounded-[100%]'
                  style={{
                    backgroundImage: `url(${item?.images && item?.images[0]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <div className='flex items-center w-full justify-between'>
                  <div>
                    <p className='text-lg font-medium'>{item.name}</p>
                    <p className='text-[#858585] w-[205px] font-medium text-xs'>
                      {item.description}
                    </p>
                  </div>
                  <div className='px-[12px] py-[8px] text-xs w-fit h-fit rounded-full text-[#595959] bg-[#EDEDEE]'>
                    {item?.cokitchen?.name}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </BaseModal>
    </div>
  );
};

export default AddNewBrandModal;
